
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import NavbarLogo from "./navbar/NavbarLogo";
import NavbarDesktopMenu from "./navbar/NavbarDesktopMenu";
import NavbarMobileMenu from "./navbar/NavbarMobileMenu";
import LanguageSwitcher from "./navbar/LanguageSwitcher";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();
  
  const isCalculatorPage = location.pathname === "/kalkulator";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSectionClick = (sectionId: string) => {
    const isHomePage = location.pathname === "/" || location.pathname === "";
    const isArbitrinityPage = location.pathname === "/arbitrinity";
    
    if (isHomePage) {
      // If on homepage, just scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else if (isArbitrinityPage && sectionId === "contact") {
      // If on Arbitrinity page and trying to access contact section
      const contactElement = document.getElementById("contact");
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If on another page, navigate to home with hash
      window.location.href = `/#${sectionId}`;
    }
    
    // Close mobile menu if open
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/80 dark:bg-gray-900/90 backdrop-blur-md shadow-subtle py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        <NavbarLogo />
        
        <div className="hidden md:flex items-center gap-8">
          <NavbarDesktopMenu 
            handleSectionClick={handleSectionClick}
            solutionsOpen={solutionsOpen}
            setSolutionsOpen={setSolutionsOpen}
          />
          
          {!isCalculatorPage && <LanguageSwitcher />}
        </div>
        
        <div className="md:hidden flex items-center gap-4">
          {!isCalculatorPage && <LanguageSwitcher />}
          
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-800 dark:text-gray-200"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {mobileMenuOpen && (
        <NavbarMobileMenu
          handleSectionClick={handleSectionClick}
          solutionsOpen={solutionsOpen}
          setSolutionsOpen={setSolutionsOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}
    </header>
  );
};

export default Navbar;
